import React from 'react';
import { Link } from 'react-router-dom';
import { Squares2X2Icon } from '@heroicons/react/24/outline';
import PurchaseButton from '../components/PurchaseButton';
import AvailabilityBadge from './AvailabilityBadge';
import AvailabilityTrend from './AvailabilityTrend';
import ProductActions from './ProductActions';
import SpecGrid from './SpecGrid';
import { ProductImage, ManufacturerLogo } from './ProductImage';
import {
  faBolt, faSitemap, faPlug, faLayerGroup, faBoxOpen,
  faTruck, faStore, faTag, faBatteryFull, faShieldHalved,
} from '@fortawesome/free-solid-svg-icons';
import { formatPrice, formatPricePerWp } from '../utils';
import { wierszeMocy } from '../utils/etykietaMocy';

const TYPE_LABEL = { panel: 'Moduł PV', inwerter: 'Falownik', magazyn: 'Magazyn' };

const orNull = (v) => (v === null || v === undefined || String(v).trim() === '' ? null : v);
const warrantyValue = (w) => {
  const v = orNull(w);
  if (v === null) return null;
  const s = String(v).trim();
  return /^\d+$/.test(s) ? `${s} lat` : s;
};
const availabilityValue = (product) => (orNull(product.availability) === null ? null : (
  <span className="inline-flex items-center gap-1">
    <AvailabilityBadge value={product.availability} />
    <AvailabilityTrend
      availability={product.availability}
      availability_prev={product.availability_prev}
      availability_prev_ts={product.availability_prev_ts}
      availability_prev2={product.availability_prev2}
      availability_prev2_ts={product.availability_prev2_ts}
    />
  </span>
));

const buildParams = (product, productType) => {
  let list = [];
  if (productType === 'panel') {
    list = [
      { icon: faBolt, label: 'Moc', value: product.power != null ? `${product.power} W` : null },
      { icon: faLayerGroup, label: 'Rodzaj', value: orNull(product.type) },
      { icon: faTag, label: 'Cena za Wp', value: orNull(formatPricePerWp(product)) },
      { icon: faStore, label: 'Sprzedawca', value: orNull(product.seller) },
      { icon: faTruck, label: 'Wysyłka', value: orNull(product.shipping) },
      { icon: faBoxOpen, label: 'Dostępność', value: availabilityValue(product) },
    ];
  } else if (productType === 'inwerter') {
    list = [
      { icon: faBolt, label: 'Moc', value: product.power != null ? `${product.power} kW` : null },
      { icon: faSitemap, label: 'Liczba MPPT', value: orNull(product.mppt) },
      { icon: faPlug, label: 'Fazy', value: orNull(product.phase) },
      { icon: faLayerGroup, label: 'Rodzaj', value: orNull(product.type) },
      { icon: faShieldHalved, label: 'Gwarancja', value: warrantyValue(product.warranty) },
      { icon: faStore, label: 'Sprzedawca', value: orNull(product.seller) },
      { icon: faTruck, label: 'Wysyłka', value: orNull(product.shipping) },
      { icon: faBoxOpen, label: 'Dostępność', value: availabilityValue(product) },
    ];
  } else if (productType === 'magazyn') {
    list = [
      { icon: faBatteryFull, label: 'Pojemność', value: product.capacity != null ? `${product.capacity} kWh` : null },
      { icon: faBolt, label: 'Napięcie', value: orNull(product.voltage) },
      { icon: faLayerGroup, label: 'Rodzaj', value: orNull(product.type) },
      { icon: faShieldHalved, label: 'Gwarancja', value: warrantyValue(product.warranty) },
      { icon: faStore, label: 'Sprzedawca', value: orNull(product.seller) },
      { icon: faTruck, label: 'Wysyłka', value: orNull(product.shipping) },
      { icon: faBoxOpen, label: 'Dostępność', value: availabilityValue(product) },
    ];
  } else if (productType === 'wallbox') {
    const zlacze = {
      gniazdo_Type2: 'Gniazdo Type 2',
      kabel_Type2: 'Kabel Type 2',
      kabel_Type1: 'Kabel Type 1',
    }[product.connector_type];
    // „Brak potwierdzonych danych" to pełnoprawna odpowiedź, nie „nie obsługuje".
    const pv = {
      wlasny_licznik: 'Tak, z dowolnym falownikiem',
      tylko_falownik_tej_marki: 'Tylko z falownikiem tej samej marki',
      brak_funkcji: 'Nie obsługuje',
      brak_danych: 'Brak potwierdzonych danych',
    }[product.pv_mode];
    const tak_nie = (v) => (v == null ? null : (v ? 'Tak' : 'Nie'));
    // Rodzaj prądu i liczba gniazd rozróżniają wallbox od stacji komercyjnej —
    // te ostatnie wchodzą do katalogu po włączeniu przełącznika w filtrach.
    const rodzaj = {
      ac_wallbox: 'Wallbox ścienny AC',
      ac_stacja: 'Stacja wolnostojąca AC',
      dc_stacja: 'Stacja szybkiego ładowania DC',
      mobilna_evse: 'Ładowarka przenośna',
    }[product.product_class];
    list = [
      { icon: faLayerGroup, label: 'Rodzaj', value: orNull(rodzaj) },
      // Stacja hybrydowa dostaje dwa osobne wiersze: Moc DC i Moc AC.
      ...wierszeMocy(product).map((w) => ({ icon: faBolt, label: w.label, value: w.value })),
      { icon: faPlug, label: 'Fazy', value: product.phases != null ? `${product.phases}-fazowy` : null },
      { icon: faBolt, label: 'Rodzaj prądu', value: orNull(product.current_type === 'nieznany' ? null : product.current_type) },
      { icon: faSitemap, label: 'Liczba gniazd', value: orNull(product.sockets_count) },
      { icon: faLayerGroup, label: 'Złącze', value: orNull(zlacze) },
      { icon: faSitemap, label: 'Długość kabla', value: product.cable_length_m != null ? `${product.cable_length_m} m` : null },
      { icon: faShieldHalved, label: 'Klasa szczelności', value: orNull(product.ip_rating) },
      { icon: faSitemap, label: 'RFID', value: tak_nie(product.rfid) },
      { icon: faBolt, label: 'Licznik MID', value: tak_nie(product.mid_meter) },
      { icon: faBolt, label: 'Ładowanie z PV', value: orNull(pv) },
      { icon: faBolt, label: 'Wymaga osprzętu', value: orNull(product.pv_wymagany_osprzet) },
      { icon: faStore, label: 'Sprzedawca', value: orNull(product.seller) },
      { icon: faBoxOpen, label: 'Dostępność', value: availabilityValue(product) },
    ];
  }
  return list.filter((p) => p.value !== null && p.value !== undefined);
};

const ProductInfo = ({ product, productType, modelSeries = null }) => {
  if (!['panel', 'inwerter', 'magazyn', 'wallbox'].includes(productType)) return null;

  const params = buildParams(product, productType);
  const grossPrice = formatPrice(product.price, 2);
  const nettoPrice = formatPrice(product.price_netto, 2);
  // Nagłówek: Typ · Producent [· Seria (tylko falowniki, jeśli przekazana)]. Brak wartości -> pomijana.
  const headerLine = [
    TYPE_LABEL[productType],
    orNull(product.manufacturer),
    productType === 'inwerter' ? orNull(modelSeries) : null,
  ].filter(Boolean).join(' · ');

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 mt-8 mb-10">
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5 md:p-7">
        <div className="grid md:grid-cols-[300px_1fr] gap-6 lg:gap-8">
          <div className="flex flex-col items-center gap-3">
            <ManufacturerLogo manufacturer={product.manufacturer} productType={productType} className="h-8" />
            <ProductImage
              src={product.img_url}
              productType={productType}
              alt={product.name}
              className="max-h-72 w-full object-contain rounded-xl border border-gray-100 bg-white p-3"
            />
          </div>

          <div className="min-w-0">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide">
                  {headerLine}
                </p>
                <h1 className="text-xl md:text-2xl font-bold text-gray-900 leading-tight mt-1">{product.name}</h1>
                {/* Usuwa zamieszanie u źródła: użytkownik przestaje się zastanawiać,
                    czemu w nazwie jest 120, a w parametrze 22. */}
                {productType === 'wallbox' && product.current_type === 'AC_DC' && (
                  <p className="mt-1 text-xs text-gray-500 italic">
                    Moc w nazwie producenta odnosi się do ładowania prądem stałym (DC).
                  </p>
                )}

              </div>
              <div className="flex-shrink-0">
                <ProductActions product={product} productType={productType} size="lg" />
              </div>
            </div>

            <div className="mt-5 pt-5 border-t border-gray-100 flex flex-col lg:flex-row gap-6">
              <div className="flex-1 min-w-0">
                <SpecGrid items={params} className="grid-cols-2 sm:grid-cols-3" />
              </div>
              <div className="lg:w-56 flex-shrink-0 lg:border-l lg:border-gray-100 lg:pl-6 flex flex-col justify-center gap-4">
                <div>
                  <p className="text-xs text-gray-400 uppercase tracking-wide">Cena</p>
                  {product.price_on_request && grossPrice === null ? (
                    <p className="text-lg md:text-xl font-semibold text-gray-600 leading-tight">Cena dla zalogowanych</p>
                  ) : (
                    <>
                      <p className="text-3xl md:text-4xl font-extrabold text-blue-600 leading-none">
                        {grossPrice !== null ? `${grossPrice} zł` : 'Brak ceny'}
                      </p>
                      {nettoPrice !== null && (
                        <p className="text-sm text-gray-400 mt-1">{nettoPrice} zł netto (bez VAT)</p>
                      )}
                    </>
                  )}
                </div>
                <PurchaseButton link={product.link} size="lg" />

                {/* Przejście do karty modelu POD przyciskiem zakupu, bo to realna
                    alternatywa dla „kup w tym sklepie": ta strona pokazuje JEDNĄ
                    ofertę, a karta modelu zbiera wszystkie oferty tego produktu,
                    historię ceny i kompatybilność. Wcześniej stało to dyskretnym
                    linkiem pod nazwą, gdzie ginęło — ktoś wchodzący z polecanych
                    brał cenę jednego sklepu za jedyną.

                    Obrys zamiast wypełnienia: ma być widoczne, ale nie konkurować
                    z głównym przyciskiem zakupu.

                    Adres z samego `model_id` — trasa modelu wyciąga id z końca
                    (`/(\d+)$/`), a serwer przekierowuje na pełny slug, więc nie
                    zgadujemy go z nazwy oferty (bywa inna niż nazwa modelu). */}
                {product.model_id != null && (
                  <Link
                    to={`/model/${productType}/${product.model_id}`}
                    className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl
                               border-2 border-blue-200 bg-white text-blue-700 text-sm font-bold
                               hover:bg-blue-50 hover:border-blue-300 transition-colors"
                  >
                    <Squares2X2Icon className="w-4 h-4" />
                    Porównaj wszystkie oferty
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductInfo;
